import React from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { experienceData } from '../mock';
import { Award, Users, Briefcase, Star } from 'lucide-react';

const Experience = () => {
  const stats = [
    {
      icon: <Briefcase size={32} />,
      value: experienceData.yearsOfExperience,
      label: 'Years Experience',
    },
    {
      icon: <Star size={32} />,
      value: `${experienceData.projectsCompleted}+`,
      label: 'Projects Completed',
    },
    {
      icon: <Users size={32} />,
      value: `${experienceData.happyClients}+`,
      label: 'Happy Clients',
    },
    {
      icon: <Award size={32} />,
      value: experienceData.awards,
      label: 'Awards Won',
    },
  ];

  const clients = [
    {
      name: 'Di-Arch Hotel',
      logo: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=200&q=80',
    },
    {
      name: 'Taj Hotel',
      logo: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=200&q=80',
    },
    {
      name: 'MKS Engineering',
      logo: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=200&q=80',
    },
    {
      name: 'ITC Grand',
      logo: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=200&q=80',
    },
  ];

  return (
    <>
      <SEO
        title="12+ Years of Photography in Lucknow | Frames by Gaurav"
        description="Explore 12+ years of wedding, event, fashion and commercial photography by Frames by Gaurav in Lucknow."
        keywords="photography experience lucknow, professional photographer experience, wedding photography expertise, award winning photographer lucknow, experienced photographer"
        canonicalUrl="/experience"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'Experience - Frames by Gaurav',
          description:
            'Over 12 years of professional photography experience with numerous awards and satisfied clients.',
          url: 'https://www.framesbygaurav.com/experience',
        }}
      />
      <Layout>
        {/* Stats Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-bold text-center mb-16 tracking-tight">
              Our Experience
            </h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center group">
                  <div className="inline-flex items-center justify-center w-20 h-20 mb-4 text-black group-hover:scale-110 transition-transform duration-300">
                    {stat.icon}
                  </div>
                  <div className="text-4xl md:text-5xl font-bold text-black mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm tracking-wider uppercase text-gray-600">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Clients & Brands Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-6 tracking-tight">
              Trusted By Leading Brands
            </h2>
            <p className="text-center text-gray-600 text-lg mb-16 max-w-2xl mx-auto">
              We&apos;ve had the privilege of working with some of the most
              prestigious hotels and venues
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {clients.map((client, index) => (
                <div
                  key={index}
                  className="aspect-square flex items-center justify-center p-6 border-2 border-gray-200 hover:border-black transition-all duration-300 group"
                >
                  <div className="text-center">
                    <div className="mb-3 overflow-hidden">
                      <img
                        src={client.logo}
                        alt={client.name}
                        className="w-full h-20 object-cover grayscale group-hover:grayscale-0 transition-all duration-300"
                      />
                    </div>
                    <p className="text-sm font-semibold text-gray-700 group-hover:text-black transition-colors">
                      {client.name}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Journey Timeline */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 tracking-tight">
              Our Journey
            </h2>
            <div className="space-y-12">
              {experienceData.journey.map((milestone, index) => (
                <div
                  key={index}
                  className="flex flex-col md:flex-row gap-8 group"
                >
                  <div className="md:w-32 flex-shrink-0">
                    <div className="text-3xl font-bold text-black">
                      {milestone.year}
                    </div>
                  </div>
                  <div className="flex-grow">
                    <div className="h-full border-l-2 border-gray-300 pl-8 pb-8 group-hover:border-black transition-colors">
                      <h3 className="text-2xl font-bold mb-3 tracking-tight">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Expertise Section */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 tracking-tight">
              Our Expertise
            </h2>
            <div className="space-y-8">
              {experienceData.expertise.map((skill, index) => (
                <div key={index} className="group">
                  <div className="flex justify-between mb-3">
                    <span className="text-lg font-semibold text-black">
                      {skill.name}
                    </span>
                    <span className="text-lg font-semibold text-gray-600">
                      {skill.level}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-300 h-2 overflow-hidden">
                    <div
                      className="h-full bg-black transition-all duration-1000 ease-out"
                      style={{ width: `${skill.level}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default Experience;
